import { useEffect, useMemo } from "react";

import {
  NetworkStatus,
  useLazyQuery,
  WatchQueryFetchPolicy,
} from "@apollo/client";

import type {
  GetLocationsQuery,
  GetLocationsQueryVariables,
  Location,
} from "../../../../../API";
import { useCustomerIdGuard } from "../../../../../common/hooks/useCustomerIdGuard";
import { GET_LOCATIONS } from "../../../../../common/operations/queries";
import { errorNotification } from "../../../../../common/variables/notification";

export const LIMIT = 25;

export const useGetLocationsCached = (
  policy: WatchQueryFetchPolicy = "cache-and-network"
) => {
  const selectedCustomerId = useCustomerIdGuard();

  const [getLocations, { data, loading, fetchMore, networkStatus }] =
    useLazyQuery<GetLocationsQuery, GetLocationsQueryVariables>(GET_LOCATIONS, {
      fetchPolicy: policy,
      notifyOnNetworkStatusChange: true,
    });

  useEffect((): void => {
    if (selectedCustomerId) {
      getLocations({
        variables: {
          limit: 300,
          customerId: selectedCustomerId,
        },
      });
    }
  }, [selectedCustomerId, getLocations]);

  useEffect(() => {
    if (!data?.getLocations?.nextToken) return;

    const fetchAllPages = async () => {
      let currentNextToken = data.getLocations.nextToken;

      try {
        while (currentNextToken) {
          const response = await fetchMore({
            variables: {
              limit: 300,
              customerId: selectedCustomerId,
              nextToken: currentNextToken,
            },
          });

          currentNextToken = response.data?.getLocations?.nextToken || "";
        }
      } catch (error) {
        console.error("Error fetching additional location pages:", error);

        errorNotification();
      }
    };

    fetchAllPages();
  }, [data?.getLocations?.nextToken, selectedCustomerId, fetchMore]);

  const locationsLoading = loading || networkStatus === NetworkStatus.fetchMore;

  const locations: Location[] = useMemo(
    (): Location[] => (data?.getLocations.items as Location[]) || [],
    [data?.getLocations.items]
  );

  return {
    locations,
    loading: locationsLoading,
  };
};
